import React from 'react';
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Add, DeleteOutline, PictureAsPdf } from '@mui/icons-material';
import {
  AUTH_NUMBER_PREFIX,
  EMPLOYMENT_TYPES,
  FORM_REFS
} from '../../constants/instructorAuthorization';
import GetsBrandHeader from '../certificationAuthorization/GetsBrandHeader';

const emptyScopeRow = () => ({
  typeOfTraining: '',
  scopeOfAuthorization: '',
  licenceLimitation: ''
});

export const emptyGetsCm001cCertificate = (overrides = {}) => ({
  dgcaApprovalNoDated: '',
  dgcaApprovalValidity: '',
  authorizationNo: AUTH_NUMBER_PREFIX,
  iaRevisionNo: '00',
  iaRevisionDate: '',
  dateOfInitialIa: '',
  validFrom: '',
  validUntil: '',
  dateOfBirth: '',
  licenseNumber: '',
  licenseExpiry: '',
  recurrentTrainingValidTill: '',
  limitations: '',
  scopeRows: [emptyScopeRow(), emptyScopeRow(), emptyScopeRow()],
  issued: true,
  ...overrides
});

/** Static Terms 1–10 from GETS/CM/001C instructor card */
const TERMS = [
  'This AUTHORIZATION is issued by GETS-MRO, a CAR 145 Approved Maintenance Organization.',
  'This authorization, issued under the provision of MOE Chapter 3.9 and E.P.M. Section-3, Training Procedure to the named personnel to authorize her/him to conduct training /examination related to course listed in on Engineering Procedure Manual, Section-3 of GETS-MRO as per the syllabus mentioned in the manual.',
  'The holder continues to be in employment / contract with GETS-MRO.',
  'The holder must ensure that his/her recurrent training is valid.',
  'The authorization is valid for the period as specified and can be withdrawn at any time if deemed necessary by the Compliance Monitoring Manager.',
  'The holder must not exercise the privileges of his/her certification authorization if it is known or suspected that his/her physical or mental condition renders him/her unfit to exercise such privileges.',
  'All previously issued authorization is hereby invalidated.',
  "The holder's Signature on this document indicated that he/she has read and understood all of the relevant provisions of GETS-MRO Compliance Monitoring System related to the issue of this authorization.",
  'The holder must ensure that the training has to be imparted in accordance with GETS-MRO approved CAR-145, MOE and relevant CAR & he/she is entitled to carryout training only within the scope of authorization.',
  'The holder must ensure that his/her authorization is valid during the course of training.'
];

const sectionSx = {
  bgcolor: '#f0d9a8',
  border: '1px solid',
  borderColor: 'text.primary',
  px: 1.5,
  py: 0.75,
  fontWeight: 700,
  textAlign: 'center',
  textTransform: 'uppercase',
  fontSize: '0.85rem',
  letterSpacing: 0.3
};

function GetsCm001cCertificateForm({
  application,
  value,
  onChange,
  readOnly = false,
  onIssue,
  onDownloadPdf,
  saving = false
}) {
  const cert = value || emptyGetsCm001cCertificate();
  const scopeRows =
    Array.isArray(cert.scopeRows) && cert.scopeRows.length ? cert.scopeRows : [emptyScopeRow()];

  const set = (patch) => onChange({ ...cert, ...patch });
  const updateScope = (index, patch) => {
    const next = scopeRows.map((row, i) => (i === index ? { ...row, ...patch } : row));
    set({ scopeRows: next });
  };

  const fieldProps = readOnly ? { InputProps: { readOnly: true } } : {};
  const dateVal = (v) => (v ? String(v).slice(0, 10) : '');

  return (
    <Paper
      variant="outlined"
      sx={{ p: { xs: 1.5, sm: 2.5 }, borderColor: 'text.primary', borderWidth: 2, borderStyle: 'double' }}
    >
      <GetsBrandHeader
        title="Instructor Authorization"
        subtitle={`Form: ${FORM_REFS.certificate} | Issue: 01 Rev 00 Dtd Jul 2026`}
      />

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        {onDownloadPdf && (
          <Button size="small" variant="outlined" startIcon={<PictureAsPdf />} onClick={onDownloadPdf}>
            Download {FORM_REFS.certificate} PDF
          </Button>
        )}
        {!readOnly && onIssue && (
          <Button size="small" variant="contained" disabled={saving} onClick={() => onIssue(true)}>
            Issue authorization
          </Button>
        )}
        {!readOnly && onIssue && (
          <Button size="small" variant="outlined" color="error" disabled={saving} onClick={() => onIssue(false)}>
            Do not issue
          </Button>
        )}
      </Stack>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            size="small"
            label="DGCA Approval No & Dated"
            value={cert.dgcaApprovalNoDated || ''}
            onChange={(e) => set({ dgcaApprovalNoDated: e.target.value })}
            {...fieldProps}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Validity"
            InputLabelProps={{ shrink: true }}
            value={dateVal(cert.dgcaApprovalValidity)}
            onChange={(e) => set({ dgcaApprovalValidity: e.target.value })}
            {...fieldProps}
          />
        </Grid>
      </Grid>

      <Grid container spacing={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
        <Grid item xs={12} md={6} sx={{ borderRight: { md: '1px solid' }, borderColor: 'divider' }}>
          <Box sx={{ ...sectionSx, border: 0, borderBottom: '1px solid', borderColor: 'divider' }}>
            Personal Details
          </Box>
          <Stack spacing={1.25} sx={{ p: 1.5 }}>
            <TextField fullWidth size="small" label="Name" value={application?.applicantName || ''} disabled />
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Date of Birth"
              InputLabelProps={{ shrink: true }}
              value={dateVal(cert.dateOfBirth)}
              onChange={(e) => set({ dateOfBirth: e.target.value })}
              {...fieldProps}
            />
            <TextField
              select={!readOnly}
              fullWidth
              size="small"
              label="Employment Type"
              value={application?.employeeType || ''}
              disabled
            >
              {EMPLOYMENT_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField fullWidth size="small" label="Employment No" value={application?.employeeId || ''} disabled />
            <TextField
              fullWidth
              size="small"
              label="LICENCE/BAMEL/BAMEEC No"
              value={cert.licenseNumber || ''}
              onChange={(e) => set({ licenseNumber: e.target.value })}
              {...fieldProps}
            />
            <TextField
              fullWidth
              size="small"
              type="date"
              label="License Expiry"
              InputLabelProps={{ shrink: true }}
              value={dateVal(cert.licenseExpiry)}
              onChange={(e) => set({ licenseExpiry: e.target.value })}
              {...fieldProps}
            />
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ ...sectionSx, border: 0, borderBottom: '1px solid', borderColor: 'divider' }}>
            Dates &amp; Validity
          </Box>
          <Stack spacing={1.25} sx={{ p: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Date of Initial IA"
              InputLabelProps={{ shrink: true }}
              value={dateVal(cert.dateOfInitialIa)}
              onChange={(e) => set({ dateOfInitialIa: e.target.value })}
              {...fieldProps}
            />
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Recurrent Training Valid Till"
              InputLabelProps={{ shrink: true }}
              value={dateVal(cert.recurrentTrainingValidTill)}
              onChange={(e) => set({ recurrentTrainingValidTill: e.target.value })}
              {...fieldProps}
            />
            <TextField
              fullWidth
              size="small"
              required
              label="Authorisation No"
              value={cert.authorizationNo || ''}
              onChange={(e) => set({ authorizationNo: e.target.value })}
              {...fieldProps}
            />
            <TextField
              fullWidth
              size="small"
              label="IA Revision No."
              value={cert.iaRevisionNo || ''}
              onChange={(e) => set({ iaRevisionNo: e.target.value })}
              {...fieldProps}
            />
            <TextField
              fullWidth
              size="small"
              type="date"
              label="IA Revision Date"
              InputLabelProps={{ shrink: true }}
              value={dateVal(cert.iaRevisionDate)}
              onChange={(e) => set({ iaRevisionDate: e.target.value })}
              {...fieldProps}
            />
            <TextField
              fullWidth
              size="small"
              type="date"
              label="IA Valid From"
              InputLabelProps={{ shrink: true }}
              value={dateVal(cert.validFrom)}
              onChange={(e) => set({ validFrom: e.target.value })}
              {...fieldProps}
            />
            <TextField
              fullWidth
              size="small"
              type="date"
              label="IA Expire on"
              InputLabelProps={{ shrink: true }}
              value={dateVal(cert.validUntil)}
              onChange={(e) => set({ validUntil: e.target.value })}
              {...fieldProps}
            />
          </Stack>
        </Grid>
      </Grid>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          Details of Authorization
        </Typography>
        {!readOnly && (
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() => set({ scopeRows: [...scopeRows, emptyScopeRow()] })}
          >
            Add row
          </Button>
        )}
      </Stack>
      <Table size="small" sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
        <TableHead>
          <TableRow sx={{ bgcolor: '#f0d9a8' }}>
            <TableCell width={48}>Sl. No</TableCell>
            <TableCell>Type of Training</TableCell>
            <TableCell>Scope of Authorization</TableCell>
            <TableCell>Licence Limitation</TableCell>
            {!readOnly && <TableCell width={40} />}
          </TableRow>
        </TableHead>
        <TableBody>
          {scopeRows.map((row, index) => (
            <TableRow key={index}>
              <TableCell>{index + 1}</TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  variant="standard"
                  value={row.typeOfTraining || ''}
                  onChange={(e) => updateScope(index, { typeOfTraining: e.target.value })}
                  {...fieldProps}
                />
              </TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  variant="standard"
                  value={row.scopeOfAuthorization || ''}
                  onChange={(e) => updateScope(index, { scopeOfAuthorization: e.target.value })}
                  {...fieldProps}
                />
              </TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  variant="standard"
                  value={row.licenceLimitation || ''}
                  onChange={(e) => updateScope(index, { licenceLimitation: e.target.value })}
                  {...fieldProps}
                />
              </TableCell>
              {!readOnly && (
                <TableCell>
                  <IconButton
                    size="small"
                    disabled={scopeRows.length <= 1}
                    onClick={() => set({ scopeRows: scopeRows.filter((_, i) => i !== index) })}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TextField
        fullWidth
        size="small"
        multiline
        minRows={2}
        label="Any Limitations"
        value={cert.limitations || ''}
        onChange={(e) => set({ limitations: e.target.value })}
        sx={{ mb: 2 }}
        {...fieldProps}
      />

      <Divider sx={{ my: 2 }} />

      <Box sx={{ ...sectionSx, mb: 1 }}>Terms of Authorisation</Box>
      <Box component="ol" sx={{ pl: 2.5, m: 0, typography: 'body2' }}>
        {TERMS.map((t, idx) => (
          <li key={idx} style={{ marginBottom: 6 }}>
            {t}
          </li>
        ))}
      </Box>

      <Grid container spacing={1} sx={{ mt: 2, mb: 2, border: '1px solid', borderColor: 'divider', p: 1.5 }}>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" fontWeight={700} display="block">
            Issued By — Compliance Monitoring Manager
          </Typography>
          <Typography variant="caption" color="text.secondary">
            SIGNATURE &amp; SEAL
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" fontWeight={700} display="block">
            Holder&apos;s Signature
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Name / Date
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  );
}

export default GetsCm001cCertificateForm;
